import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { useHistory } from 'react-router-dom';
import _ from 'lodash'
import { format, sub, eachDayOfInterval } from 'date-fns'

import { Button, PopOut, Chart } from 'components'
import { PageBlock, PageInput, ItemBlock, ItemModal, DayModal } from 'features'
import { FaPlus } from 'react-icons/fa'

import * as actionCreators from 'store/actions'
import * as api from 'api'
import { dark, light } from 'theme'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    min-height: calc(100vh - 2.5rem);
    padding: 5rem var(--main-padding-width-small);
    @media (max-width: 1000px) {
        padding: 5rem 2rem;
    }
`

const Title = styled.div`
    font-weight: bold;
    font-size: 2.5rem;
    text-align: center;
`

const Subtitle = styled.div`
    font-size: 0.9rem;
    text-align: center;
`

const ChartContainer = styled.div`
    /* border: 1px solid var(--secondary-color); */
    border-radius: 5px;
    /* padding: 1rem; */
    width: 100%;
    min-height: 30vh;
    /* margin-top: 2rem; */
    position: relative;
`

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 1rem));
    grid-column-gap: 1rem;
    width: 100%;
    margin: 2rem 0;
    @media (max-width: 1000px) {
        grid-template-columns: 100%;
        grid-template-rows: repeat(2, auto);
        grid-row-gap: 1rem;
    }
`

const GridItem = styled.div`
    border-radius: 5px;
    padding: 1rem;
    border: 1px solid var(--secondary-color);
    max-height: 50vh;
    overflow-y: auto;
    min-height: 30vh;
    box-shadow: var(--box-shadow);
    @media (max-width: 1000px) {
        min-height: auto;
    }
`

const GridTitle = styled.div`
    font-weight: bold;
    /* text-transform: uppercase; */
    /* color: var(--secondary-accent-color); */
    margin: ${props => props.margin || '0 0 1rem 0.6rem'};
    font-size: 1rem;
`

const PageContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 0.5rem));
    grid-column-gap: 0.5rem;
    grid-row-gap: 0.5rem;
`

const PageButton = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-column-gap: 0.5rem;
    align-items: center;
    justify-content: center;
    /* background-color: var(--secondary-color); */
    border: 2px dashed var(--secondary-color);
    border-radius: 5px;
    cursor: pointer;
    color: var(--secondary-accent-color);
    padding: 1rem;
    height: 100%;
    font-size: 0.8rem;
    font-weight: bold;
    :hover {
        filter: brightness(var(--brightness));
    }
`

const Placeholder = styled.div`
    color: var(--secondary-accent-color);
    margin: 0.5rem;
`

const StatsContainer = styled.div`
    padding: 0.25rem;
    display: grid;
    grid-template-columns: auto auto auto auto;
`

const Divider = styled.div`
    border: 1px solid var(--secondary-color);
    margin: 0 1rem 0 0;
`

const HomeModule = props => {
    const {} = props;
    const dispatch = useDispatch()
    let history = useHistory()
    const user = useSelector(state => state.auth.user)
    const themeRef = user['theme'] === 'Dark' ? dark : light
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const created = useSelector(state => state.auth.created)
    const completed = useSelector(state => state.auth.completed)
    const incomplete = useSelector(state => state.auth.incomplete)
    const [scheduledData, setScheduled] = React.useState({})
    const [completedData, setCompleted] = React.useState({})

    React.useEffect(() => {
        dispatch(actionCreators.getUserCount(user['uid']))
        api.countItems({ userId: user['uid']})
        .then(countRes => {
            // console.log('Created',countRes.data['outcome'])
            setScheduled(countRes.data['outcome'])
        })
        api.countItems({ userId: user['uid'], completed: true})
        .then(countRes => {
            // console.log('Completed',countRes.data['outcome'])
            const outcome = countRes.data['outcome']
            delete outcome['null']
            setCompleted(outcome)
        })

        // Get items
        dispatch(actionCreators.loadItems(data, {
            "logic": "OR",
            userID: user['uid'],
            "filters": {
                end_date: {
                    "$gte": moment().subtract(30, 'days').toDate(),
                    "$lte": moment().toDate()
                },
                completed_date: {
                    "$gte": moment().subtract(30, 'days').toDate(),
                    "$lte": moment().toDate()
                }
            }
        }))
    }, [])

    const onSelectPage = (pageID) => {
        const oldUser = {...user}
        let updatedUser = {...user}
        updatedUser['activePageID'] = pageID
        updatedUser['activePageType'] = 'Pages'
        dispatch(actionCreators.updateUser(updatedUser, oldUser))
        history.push(pageID.toLowerCase())
        window.scrollTo(0,0)
    }

    const onSelectChart = ({ label, index, values }) => {
        const start = sub(new Date(), {months: 1})
        const end = new Date()
        const interval = eachDayOfInterval({ start: start, end: end })
        const labels = interval.map(date => format(date, 'yyyy-MM-dd'))
        const selected = labels[index]
        // console.log({selected, index, values})
        const completed = values.find(y => y['label'] === 'completed')['data'][index]
        const scheduled = values.find(y => y['label'] === 'scheduled')['data'][index]
        dispatch(actionCreators.setModal(<DayModal date={selected} values={values} index={index}/>, `DayModal_${selected}`))
    }

    const getPages = () => {
        const pages = data['pages']
        let blocks = [
            <PopOut trigger='leftClick' placement='bottom-start' animation='default' element={<div style={{margin: '0.5rem 0'}}><PageInput/></div>}>
                <PageButton>
                    <FaPlus/>
                    <div>Create page</div>
                </PageButton>
            </PopOut>
        ]
        Object.values(['activePages','archivePages']).forEach(status => {
            user[status].forEach(pageID => blocks.push(
                <PageBlock page={pages[pageID]} type='TILE' 
                    onSelect={() => onSelectPage(pageID)}
                    style={{backgroundColor: 'var(--primary-color)'}}/>
            ))
        })
        return <PageContainer>{blocks}</PageContainer>
    }

    const getItems = () => {
        let blocks = []
        const todayDate = moment().format('YYYY-MM-DD')
        const tomorrowDate = moment().add(1, 'days').format('YYYY-MM-DD')
        let today = Object.values(data['items']).filter(item => {
            const startMatch = item['start_date'] && moment(item['start_date']).format('YYYY-MM-DD') === todayDate
            const endMatch = item['end_date'] && moment(item['end_date']).format('YYYY-MM-DD') === todayDate
            return startMatch || endMatch
        })
        today = _.sortBy(today, ['status']).reverse()
        today = _.sortBy(today, ['start_time','end_time','start_date','end_date'])

        blocks.push(<GridTitle margin='0 0 0 0.6rem'>⏰ Upcoming today</GridTitle>)
        if(today.length > 0){
            today.forEach(item => blocks.push(
                <ItemBlock key={item['itemID']} itemID={item['itemID']} disableButtons pageName type='SEARCH'
                    onSelect={() => {dispatch(actionCreators.setModal(<ItemModal itemID={item['itemID']}/>, `${item.itemID}_ItemModal`))}}/>
            ))
        } else {
            blocks.push(<Placeholder>No upcoming items today</Placeholder>)
        }
        

        let tomorrow = Object.values(data['items']).filter(item => {
            const startMatch = item['start_date'] && moment(item['start_date']).format('YYYY-MM-DD') === tomorrowDate
            const endMatch = item['end_date'] && moment(item['end_date']).format('YYYY-MM-DD') === tomorrowDate
            return startMatch || endMatch
        })
        tomorrow = _.sortBy(tomorrow, ['status']).reverse()
        tomorrow = _.sortBy(tomorrow, ['start_time','end_time','start_date','end_date'])

        blocks.push(<GridTitle margin='1rem 0 0 0.6rem'>🗓 Tomorrow</GridTitle>)
        if(tomorrow.length > 0){
            tomorrow.forEach(item => blocks.push(
                <ItemBlock key={item['itemID']} itemID={item['itemID']} disableButtons pageName type='SEARCH'
                    onSelect={() => {dispatch(actionCreators.setModal(<ItemModal itemID={item['itemID']}/>, `${item.itemID}_ItemModal`))}}/>
            ))
        } else {
            blocks.push(<Placeholder>No items tomorrow</Placeholder>)
        }

        // console.log({today, tomorrow})
        return blocks
    }

    const getChart = () => {
        const start = sub(new Date(), {months: 1})
        const end = new Date()
        const interval = eachDayOfInterval({ start: start, end: end })
        let ref = {
            scheduled: scheduledData || {},
            completed: completedData || {}
        }
        let data = {
            scheduled: [],
            completed: []
        }
        const labels = interval.map(date => format(date, 'eee,d-MMM'))
        interval.forEach(date => {
            const x = format(date, 'yyyy-MM-dd')
            Object.keys(data).forEach(key => {
                data[key].push(ref[key][x] ? ref[key][x] : 0)
            })
        })

        return <Chart type='LINE' theme={user['theme']} 
                labels={labels}
                onSelect={onSelectChart}
                data={[
                    {
                        label: 'completed',
                        data: data['completed'],
                        borderColor: themeRef['--focus-color'],
                        backgroundColor: themeRef['--focus-color'],
                        yAxisLabel: 'completed',
                        yMax: Math.floor((Math.max(...data['scheduled']) + 10) / 10) * 10,
                    },
                    {
                        label: 'scheduled',
                        data: data['scheduled'],
                        borderColor: themeRef['--focus-color2'],
                        backgroundColor: themeRef['--focus-color2'],
                        yMax: Math.floor((Math.max(...data['scheduled']) + 10) / 10) * 10,
                        yAxisLabel: 'scheduled'
                    },
                ]}/>
    }

    const getStats = () => {
        let statsBlocks = []
        const stats = {
            'Created': created,
            'Completed': completed,
            'In progress': incomplete
        }
        for (const [key, value] of Object.entries(stats)) {
            statsBlocks.push(
                        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', marginRight: key !== 'In progress' && '1rem'}}>
                            <div style={{fontWeight: 'bolder', fontSize: '1.2rem'}}>{value || '-'}</div>
                            <div style={{fontSize: '0.8rem', color: 'var(--secondary-accent-color)', whiteSpace: 'nowrap', marginTop: '0.2rem'}}>{key}</div>
                        </div>
            )
        }
        statsBlocks.splice(1,0,<Divider/>)

        return statsBlocks
    }
    

    const dateTitle = moment().format('dddd, D MMMM')
    const hour = new Date().getHours()
    const title = hour < 12 ? `Good morning ${user['first_name']}`
                : hour < 18 ? `Good afternoon ${user['first_name']}` 
                : `Good evening ${user['first_name']}`
    return (
        <Container>
            <Subtitle>{dateTitle}</Subtitle>
            <Title>{title}</Title>
            <StatsContainer>{getStats()}</StatsContainer>
            <ChartContainer>{getChart()}</ChartContainer>

            <GridContainer>
                <GridItem>
                    <GridTitle>📝 Pages</GridTitle>
                    {getPages()}
                </GridItem>
                <GridItem>
                    {getItems()}
                </GridItem>
            </GridContainer>
        </Container>
    )
}

export default HomeModule